import { Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/hooks/useAuth";
import { LoginForm } from "@/components/auth/LoginForm";
import {
  GoogleAuthButton,
  OrEmailDivider,
} from "@/components/auth/GoogleAuthButton";
import { LanguageSwitcher } from "@/components/layout/LanguageSwitcher";

// SSOButtons (per-org workspace SSO) is intentionally NOT imported
// here. That component is reachable via /login/sso?org=<slug> when an
// admin emails the workspace link. The login page used to surface a
// non-functional "Find SSO options" button which confused new users.
// The "Sign in with Google" button below is the consumer-facing
// single-button OAuth path — distinct from the per-org enterprise
// SSO flow (which is plan-gated behind ``sso``).

export function LoginPage() {
  const { isAuthenticated } = useAuth();
  const { t } = useTranslation();

  if (isAuthenticated) return <Navigate to="/" replace />;

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left panel — branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-primary-500 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary-500 via-primary-700 to-accent/20" />
        <div className="relative z-10 flex flex-col justify-center px-16 text-white">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <img
              src="/logo-full-white.png"
              alt="Lenavio"
              className="h-14 w-auto"
            />
            <p className="text-lg text-white/70 mt-4 max-w-md">
              {t("auth.tagline")}
            </p>
          </motion.div>

          {/* Decorative circles */}
          <div className="absolute -bottom-20 -right-20 w-64 h-64 rounded-full bg-accent/10" />
          <div className="absolute top-20 -right-10 w-32 h-32 rounded-full bg-accent/5" />
        </div>
      </div>

      {/* Right panel — login form */}
      <div className="flex-1 flex items-center justify-center px-6 py-12 relative">
        {/* Language switcher — available pre-login so non-English speakers
            can change the UI language before signing in. */}
        <div className="absolute top-4 right-4">
          <LanguageSwitcher />
        </div>
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
          className="w-full max-w-sm"
        >
          {/* Mobile logo */}
          <div className="lg:hidden flex justify-center mb-8">
            <img src="/logo.png" alt="Lenavio" className="h-10 w-auto" />
          </div>

          <div className="text-center mb-8">
            <h2 className="text-2xl font-semibold font-heading text-text-primary">
              {t("auth.welcomeBack")}
            </h2>
            <p className="text-sm text-text-muted mt-1">
              {t("auth.signInSubtitle")}
            </p>
          </div>

          {/*
            Google button on top, divider, then email/password. Order
            matters — putting Google first matches the dominant SaaS
            convention (Notion, Linear, Vercel, etc.) and is the
            visitor's lowest-friction option (one click vs. four
            fields). The OrEmailDivider component keeps the spacing
            consistent with the Register surface.
          */}
          <GoogleAuthButton mode="signin" />
          <OrEmailDivider />

          <LoginForm />

          {/* PLG entry — points visitors without an account at the
              marketing-aligned /register page rather than the internal
              /signup flow used by direct admin invites. */}
          <div className="mt-6 text-center text-sm text-text-muted">
            Don&apos;t have an account?{" "}
            <Link to="/register" className="text-accent hover:underline">
              Start 3-day trial
            </Link>
          </div>
        </motion.div>

        {/* Legal footer links — visible without a session so visitors can
            read the privacy policy and terms before signing in. */}
        <div className="absolute bottom-4 left-0 right-0 flex items-center justify-center gap-4 text-xs text-text-muted">
          <Link to="/privacy" className="hover:text-text-primary transition-colors">
            {t("legal.privacy", "Privacy")}
          </Link>
          <span aria-hidden="true">·</span>
          <Link to="/terms" className="hover:text-text-primary transition-colors">
            {t("legal.terms", "Terms")}
          </Link>
          <span aria-hidden="true">·</span>
          <Link to="/security" className="hover:text-text-primary transition-colors">
            {t("legal.trust", "Trust &amp; security")}
          </Link>
        </div>
      </div>
    </div>
  );
}
